<template>
    <div class="container nopadding">
        <div class="wrapper">
            <div class="header"><h1>{{$t('prenotazione_tavolo.richiesta')}}</h1></div>
            <div class="content text-center" style="min-height: 580px;">
                <reserve-confirmation
                    v-if="this.booked"
                    :confirmed="this.confirmed"
                />
                <div v-else>
                    <reserve-content v-if="!this.fetching && !this.curr_status"
                                     :people-number="this.conf_people_number"
                                     :days="this.conf_days"
                                     :f-acconto="this.conf_down_payment"
                                     :acconti="this.conf_acconti"
                                     :privacy-checkboxes="this.privacy_checkboxes"
                                     :payment-modes="this.payment_modes"
                                     :user-info="this.user_info"
                                     @onBooked="this.onBooked"
                    />
                </div>
                <div v-if="!!this.curr_status">
                    <h3 class="p-5"><i>{{this.curr_status}}</i></h3>
                </div>
                <div class="spinner-border pb-3 my-5" role="status" v-if="this.fetching">
                    <span class="sr-only">Loading...</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {mapActions, mapState} from "vuex";

import {lv1 as getLv1, lv2 as getLv2} from "../../api/restaurant/shifts";
import {getMixedInfo, getPrivacyCheckboxes} from "../../api/restaurant";
import {getSummary, stripeGetPaymentMethods} from "../../api/account";
import ReserveContent from "./partials/ReserveContent";
import ReserveConfirmation from "./partials/ReserveConfirmation";

export default {
    name: "Reserve",
    components: {ReserveConfirmation, ReserveContent},
    data() {
        return {
            curr_status: null,
            privacy_checkboxes: [],
            user_info: null,
            payment_modes: [],
            conf_days: null,
            conf_people_number: null,
            conf_down_payment: null,
            conf_acconti: [],
            fetching: true,
            booked: null,
            confirmed: null,
        };
    },
    created() {
        this.fetch();
    },
    computed: {
        ...mapState('localization', {
            language: state => state.language
        }),
        ...mapState('restaurant', {
            restaurant_id: state => state.id,
            locale: state => state.locale
        }),
        ...mapState('auth', {
            user: state => state
        })
    },
    methods: {
        fetch: async function () {
            this.showLoading(true);

            try {
                const confPrenotazioni = (
                    await getLv1(this.language, {
                        id_ristorante: this.restaurant_id,
                        id_locale: this.locale.id,
                        tipo: 'PRENOTAZIONI',
                    })
                ).prenotazioni;

                let status = null;
                if (!confPrenotazioni.f_attivo || confPrenotazioni.f_forza_blocco) {
                    status = this.$t('prenotazioni_tavolo.bloccate');
                }

                if (status) {
                    this.curr_status = status;
                    this.fetching = false;
                    this.showLoading(false);
                    return;
                }

                const days = (
                    await getLv2(this.language, {
                        id_ristorante: this.restaurant_id,
                        id_locale: this.locale.id,
                        tipo: 'PRENOTAZIONI',
                    })
                ).giorni;

                if (!days.length) {
                    this.curr_status = 'Nessun giorno disponibile';
                    this.fetching = false;
                    this.showLoading(false);
                    return;
                }

                if (days.length) {
                    this.privacy_checkboxes = ( // Checkbox dinamiche della privacy
                        await getPrivacyCheckboxes(this.language, {
                            id_ristorante: this.restaurant_id,
                            id_locale: this.locale.id,
                            for_reservation: true,
                        })
                    ).checkboxes;

                    // Informazioni automatiche cliente
                    if (this.user.email) {
                        const summary = await getSummary(this.language, {
                            id_ristorante: this.restaurant_id,
                            email: this.user.email,
                            api_key: this.user.api_key,
                        });

                        this.user_info = {
                            email: summary.cliente.email,
                            phone: summary.cliente.telefono,
                            name: summary.cliente.nome + ' ' + summary.cliente.cognome,
                            privacy: 1,
                        };
                    }

                    let stripePaymentMethods = [];
                    if (this.user.api_key) {
                        stripePaymentMethods = (
                            await stripeGetPaymentMethods(this.language, {
                                id_ristorante: this.restaurant_id,
                                id_locale: this.locale.id,
                                api_key: this.user.api_key,
                            })
                        ).methods;
                    }

                    // Modalità di pagamento
                    const mixedInfo = await getMixedInfo(this.language, {
                        id_ristorante: this.restaurant_id,
                        id_locale: this.locale.id,
                        modalita_pagamento: true,
                        credenziali_pagamento: true,
                    });
                    let idModalitaPagamento = 1;

                    let modalitaPagamento = [];
                    mixedInfo.modalita_pagamento.forEach((obj) => {
                        switch (obj.tipo) {
                            case 'STRIPE_CARTACREDITO':
                                if (stripePaymentMethods.length) {
                                    stripePaymentMethods.forEach((spm) => {
                                        modalitaPagamento.push({
                                            id: idModalitaPagamento++,
                                            tipo: obj.tipo,
                                            label: null,
                                            info: spm,
                                            credenziali: obj.credenziali,
                                        });
                                    });
                                } else {
                                    modalitaPagamento.push({
                                        id: idModalitaPagamento++,
                                        tipo: obj.tipo,
                                        label: this.$t('carrello_pag_carte'),
                                        info: null,
                                        credenziali: obj.credenziali,
                                    });
                                }
                                break;
                            case 'PAYPAL':
                                modalitaPagamento.push({
                                    id: idModalitaPagamento++,
                                    tipo: obj.tipo,
                                    label: 'PayPal',
                                    info: null
                                });
                                break;
                        }
                    });
                    this.payment_modes = modalitaPagamento;

                    this.conf_days = days;
                    this.conf_people_number = confPrenotazioni.numero_persone
                        ? confPrenotazioni.numero_persone
                        : 20;
                    if (confPrenotazioni.f_acconto) {
                        this.conf_down_payment = confPrenotazioni.f_acconto;
                        this.conf_acconti = confPrenotazioni.acconti;
                    }

                    this.fetching = false;
                    this.showLoading(false);
                } else {
                    this.curr_status = status;
                    this.fetching = false;
                    this.showLoading(false);
                }
            } catch (e) {
                this.fetching = false;
                this.showLoading(false);
            }
        },
        onBooked: function (success, confirmed) {
            this.booked = success;
            this.confirmed = confirmed;
        },
        ...mapActions('global', {
            showLoading: 'showLoading'
        })
    }
}
</script>
